import { useAuth } from "../contexts/AuthContext";

/**
 * Hook that provides permission checks for the current user.
 * Owner → full access
 * Admin → full config access + profile restrictions on CRM modules (if profile assigned)
 * Member → fully controlled by assigned profile
 */
export function usePermissions() {
  const { user } = useAuth();

  const isOwner = user?.role === "owner";
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  /**
   * Check if user can perform an action on a module.
   * @param {string} module - negocios | empresas | contatos | produtos | dashboard
   * @param {string} action - view | create | edit | delete | export | mover_cards | ganhar_perder | somente_proprios
   */
  const can = (module, action) => {
    if (!user) return false;
    if (user.role === "owner") return true;
    if (user.role === "admin") {
      // Admin always has full config access
      if (module === "config") return true;
      // If admin has no profile: full access to CRM modules
      if (!user._permissions) return true;
      const perms = user._permissions?.[module];
      if (!perms) return true;
      // If permission is explicitly false, deny; otherwise allow
      return perms[action] !== false;
    }
    // Member: must have profile
    if (!user._permissions) return false;
    const perms = user._permissions?.[module];
    if (!perms) return false;
    return perms[action] === true;
  };

  /**
   * Check config section access.
   * @param {string} section - campos | pipeline | perfis | usuarios | aparencia | auditoria
   */
  const canConfig = (section) => {
    if (!user) return false;
    if (user.role === "owner") return true;
    if (user.role === "admin") {
      // Admin always can manage usuarios and perfis
      if (["usuarios", "perfis"].includes(section)) return true;
      if (!user._permissions) return true;
      return user._permissions?.config?.[section] !== false;
    }
    return user._permissions?.config?.[section] === true;
  };

  /**
   * Returns the list of allowed pipeline IDs, or null if all pipelines are accessible.
   */
  const getAllowedPipelines = () => {
    if (!user) return [];
    if (user.role === "owner" || user.role === "admin") return null; // null = all
    return user._permissions?.negocios?.pipelines_permitidos ?? null;
  };

  /**
   * Whether the user should only see their own records in negocios.
   */
  const isSomenteProprioNegocios = () => {
    if (!user) return false;
    if (user.role === "owner" || user.role === "admin") return false;
    return user._permissions?.negocios?.somente_proprios === true;
  };

  return {
    can,
    canConfig,
    getAllowedPipelines,
    isSomenteProprioNegocios,
    isOwner,
    isAdmin,
    hasNoProfile: user?.no_profile === true,
  };
}
